import { useAuthContext } from "contexts/AuthContext"
import { useGlobalAlert } from "contexts/GlobalAlertContext"
import { useState } from "react"
import { studentRequest } from "services/requests"

const prefix = "placement"

const useApi = () => {
  const [isFetching, setIsFetching] = useState(false)
  const { showAlert } = useGlobalAlert()
  const { selectedGroupData } = useAuthContext()

  const getPlacements = async ({
    placementLanguage,
    translationsLanguageId,
  }) => {
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/${placementLanguage}/${translationsLanguageId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getModules = async ({ placementId, translationsLanguageId }) => {
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/modules/${placementId}/${translationsLanguageId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getActivityContent = async ({
    moduleId,
    languageId,
    placementTestId = 0,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/module/${moduleId}/${languageId}${
        placementTestId > 0 ? "?placementTestId=" + placementTestId : ""
      }`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getExercises = async ({
    moduleId,
    activityId,
    languageId,
    placementTestId = 0,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/exercise/${moduleId}/${activityId}/${languageId}${
        placementTestId > 0 ? "?placementTestId=" + placementTestId : ""
      }`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const addTest = async ({
    placementId,
    userId,
    grade,
    progress,
    levelObtained,
    isSelected,
    results,
    autoAssignToGroup,
    isFinished,
    version = 2,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/addTest?addResults=false`,
      body: {
        placementId,
        userId,
        grade,
        progress,
        levelObtained,
        isSelected,
        results,
        autoAssignToGroup,
        isFinished,
        version,
      },
      method: "post",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const editTest = async ({
    placementTestId,
    placementId,
    userId,
    grade,
    progress,
    levelObtained,
    isSelected,
    isFinished,
    autoAssignToGroup,
    results,
    version = 2,
    logs = [],
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/editTest?checkResults=true`,
      body: {
        placementTestId,
        placementId,
        userId,
        grade,
        progress,
        levelObtained,
        isSelected,
        results,
        autoAssignToGroup,
        isFinished,
        version,
        logs,
      },
      method: "post",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const addGrade = async ({
    placementTestId,
    moduleActivityId,
    exerciseId,
    grade,
    answers,
    files,
    loadDetails = false,
    filesFromPartialGrade = false,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/grade/add?loadDetails=${loadDetails}`,
      body: {
        placementTestId,
        moduleActivityId,
        exerciseId,
        grade,
        answers,
        filesFromPartialGrade,
      },
      method: "post",
    })
    if (!response.success)
      console.log(response.message || "Something went wrong")
    else {
      const placementTestGradeId = response?.data?.placementTestGradeId
      if (files?.length > 0) {
        const formData = new FormData()
        files.forEach((file) => {
          formData.append("files", file)
        })
        const response = await studentRequest.sendRequest({
          endpoint: `${prefix}/grade/upload/${placementTestGradeId}`,
          body: formData,
          method: "post",
        })
        if (!response.success)
          console.log(response.message || "Something went wrong")
      }
    }
    setIsFetching(false)
    if (loadDetails) {
      return {
        ...response,
        data: response.data?.details?.data,
      }
    }
    return response
  }

  const getPlacementLevels = async ({ placementLanguage }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/levels/${placementLanguage}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getUserStatus = async ({ groupId, placementLanguageId = 0 }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/user/status/${groupId}/${placementLanguageId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getUserReminder = async ({ groupId }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/user/reminder/${groupId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getUserGrades = async ({ groupId, placementId = 0 }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/user/grades/${groupId}?placementId=${placementId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getPlacementsCompleted = async ({ groupId }) => {
    setIsFetching(true)
    const group = groupId ? `?groupId=${groupId}` : ""
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/user/placementsCompleted${group}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const joinPlacementToGroup = async ({
    placementId,
    levelObtained,
    replace,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/joinToPlacementGroup/${placementId}/${levelObtained}/${replace}`,
      body: {},
      method: "post",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const resumePlacementTest = async ({ placementTestId }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/resume/${placementTestId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const checkDetails = async ({ placementTestId }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/checkDetails?placementTestId=${placementTestId}&solveProblems=false`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(response.message || "Something went wrong", teacherImage)
    setIsFetching(false)
    return response.data
  }

  const addStatusLog = async ({
    placementTestResultId,
    statusId,
    moveToModuleId,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/addStatusLog?placementTestResultId=${placementTestResultId}&statusId=${statusId}${
        moveToModuleId ? `&moveToModuleId=${moveToModuleId}` : ""
      }`,
      body: null,
      method: "get",
    })
    if (!response.success) {
      console.log(response.message)
    }
    setIsFetching(false)
    return response.data
  }

  return {
    getPlacements,
    getModules,
    getActivityContent,
    getExercises,
    addTest,
    editTest,
    addGrade,
    getPlacementLevels,
    getUserStatus,
    getUserReminder,
    getUserGrades,
    getPlacementsCompleted,
    joinPlacementToGroup,
    resumePlacementTest,
    checkDetails,
    addStatusLog,
    isFetching,
  }
}

export default useApi
